import { Button, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { useGetBranches } from "hooks/useGetBranches";
import { useGetCategories } from "hooks/useGetCategories";
import { useTranslation } from "react-i18next";
import { TicketType } from "interfaces/entities/ticket.model";
import { TicketsPeriodPreset, TicketsQueryParams } from "hooks/useTicketsQueryParams";

interface TicketsFilterBarProps {
  value: TicketsQueryParams;
  onChange: (partial: Partial<TicketsQueryParams>) => void;
  onReset: () => void;
}

const PERIOD_OPTIONS: Array<{ value: TicketsPeriodPreset; label: string }> = [
  { value: "all", label: "Все время" },
  { value: "year_to_date", label: "С начала года" },
  { value: "previous_year", label: "За прошлый год" },
  { value: "custom", label: "Выбрать диапазон" },
];

const toItems = (resp: any): any[] =>
  resp?.rows || resp?.data || (Array.isArray(resp) ? resp : []) || [];

export const TicketsFilterBar = ({ value, onChange, onReset }: TicketsFilterBarProps) => {
  const { t } = useTranslation();
  const orgID = localStorage.getItem("orgID") || "";

  const { data: branchesResponse } = useGetBranches({ orgID });
  const { data: categoriesResponse } = useGetCategories({
    orgID,
    type: "TICKET",
  });

  const branches = toItems(branchesResponse);
  const categories = toItems(categoriesResponse);

  // Любое изменение фильтра сбрасывает page на 1.
  const setFilter = (partial: Partial<TicketsQueryParams>) => onChange({ ...partial, page: 1 });

  // Локальный буфер для TextField "Поездка" — чтобы не дёргать запрос на каждую клавишу.
  // Синхронизируется с value.trainNumber (например, при переходе с графика).
  const [trainInput, setTrainInput] = useState(value.trainNumber);
  useEffect(() => {
    setTrainInput(value.trainNumber);
  }, [value.trainNumber]);
  const commitTrain = () => {
    if (trainInput !== value.trainNumber) setFilter({ trainNumber: trainInput });
  };

  return (
    <div className="flex flex-wrap gap-3 items-center mb-4">
      {/* Филиал */}
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel>Филиал</InputLabel>
        <Select
          label="Филиал"
          value={value.branchID}
          onChange={(e) => setFilter({ branchID: String(e.target.value) })}
        >
          <MenuItem value="">Все</MenuItem>
          {branches.map((b: any) => (
            <MenuItem key={b.id} value={b.id}>
              {b.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Поезд — фильтр по Ticket.data.trainNumber (свободный ввод). */}
      <TextField
        size="small"
        label="Поезд (№)"
        value={trainInput}
        onChange={(e) => setTrainInput(e.target.value)}
        onBlur={commitTrain}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            (e.target as HTMLInputElement).blur();
          }
        }}
        sx={{ minWidth: 180 }}
      />

      {/* Тип обращения */}
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel>Тип</InputLabel>
        <Select
          label="Тип"
          value={value.type}
          onChange={(e) => setFilter({ type: String(e.target.value) })}
        >
          <MenuItem value="">Все</MenuItem>
          {Object.values(TicketType).map((tt) => (
            <MenuItem key={tt} value={tt}>
              {t(`ticket.type.${tt}`)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Тематика */}
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel>Тематика</InputLabel>
        <Select
          label="Тематика"
          value={value.categoryID}
          onChange={(e) => setFilter({ categoryID: String(e.target.value) })}
        >
          <MenuItem value="">Все</MenuItem>
          {categories.map((c: any) => (
            <MenuItem key={c.id} value={c.id}>
              {c.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Период */}
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <InputLabel>Период</InputLabel>
        <Select
          label="Период"
          value={value.period}
          onChange={(e) =>
            setFilter({
              period: e.target.value as TicketsPeriodPreset,
              from: "",
              to: "",
            })
          }
        >
          {PERIOD_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Custom from/to */}
      {value.period === "custom" && (
        <>
          <TextField
            size="small"
            type="date"
            label="С"
            InputLabelProps={{ shrink: true }}
            value={value.from}
            onChange={(e) => setFilter({ from: e.target.value })}
            sx={{ minWidth: 150 }}
          />
          <TextField
            size="small"
            type="date"
            label="По"
            InputLabelProps={{ shrink: true }}
            value={value.to}
            onChange={(e) => setFilter({ to: e.target.value })}
            sx={{ minWidth: 150 }}
          />
        </>
      )}

      <Button size="small" onClick={onReset}>
        Сбросить
      </Button>
    </div>
  );
};
